import type { IFeeSplitLine } from 'components/FeeSplits'
import { BaseFile } from 'config'

export interface ILoanCharge {
  id: number
  loanNumber: number
  date: string
  description: string
  /** Fee category value; '' on charges booked before categories existed. */
  category: string
  /** Who is paid out of this charge when it is collected, and how much of it. */
  splits?: IFeeSplitLine[]
  /** Which side of the book the charge sits on. Absent on charges booked before lender charges. */
  owedBy?: LoanChargeOwedBy
  /** The lender who owes a lender charge; 0 when they have no investor record of their own. */
  lenderParticipantUserId?: number
  lenderName?: string
  /** Set once a lender charge has been fully recovered from that lender's distributions. */
  isPaid?: boolean
  /**
   * Whether the charge was waived IN FULL - forgiven rather than collected, with nothing further
   * collectible. A fully waived charge keeps its original balance, date, category and description
   * as the record of the fee that was charged, while its unpaid balance, accrued interest and
   * total due are zeroed so nothing bills it. A charge only PART of which was waived is not
   * flagged here, because it still bills what is left; `waivedAmount` is what tells them apart.
   */
  waived?: boolean
  /** Date of the waiver, as YYYY-MM-DD. */
  waivedDate?: string
  /**
   * What has been forgiven on the charge: on a full waiver the balance plus the accrued interest
   * outstanding at the time, and on a partial one the amount taken off the balance. It accumulates
   * over several waivers, and is what reversing one puts back.
   */
  waivedAmount?: number
  /** Email of the user who waived it. */
  waivedBy?: string
  waivedReason?: string
  interestRate: number
  interestFromDate: string
  deferred: boolean
  /**
   * Why the charge is being carried rather than collected now - the deferral's counterpart to
   * `waivedReason`. Only kept while `deferred` is on: the server clears it when the deferral is
   * lifted, so it never explains something that is no longer in force.
   */
  deferredReason?: string
  owedToAccount: string
  originalBalance: number
  unpaidBalance: number
  accruedInterest: number
  totalDue: number
  files: BaseFile[]
  createdAt: Date
  updatedAt: Date
  byteproFileName?: number
}

/**
 * Whether there is anything left on a charge to waive.
 *
 * Shared by the grid's waive icon and the charge form's Waive button so the two can never disagree
 * about whether the action is available - a servicer who is told on one screen that a charge can
 * be waived and finds no way to do it on the other has no way to tell which is right.
 *
 * Both halves of what is owed count: a charge can carry booked interest over a zero balance, and
 * that interest is still collectable and so still waivable.
 */
export const canWaiveCharge = (item: ILoanCharge) =>
  !item.waived && Number(item.unpaidBalance || 0) + Number(item.accruedInterest || 0) > 0

/** Whether any of the charge has already been waived, in full or in part. */
export const isChargeWaived = (item: ILoanCharge) => !!item.waived || Number(item.waivedAmount || 0) > 0

/**
 * Who owes a charge. A borrower charge bills the borrower and settles when they pay; a lender
 * charge is owed by the investor holding the loan and is netted out of what they are next
 * distributed, so it never reaches the borrower. Must match the backend's LoanChargeOwedBy.
 */
export enum LoanChargeOwedBy {
  Borrower = 'borrower',
  Lender = 'lender',
}

/** A lender a charge can be booked against, as `GET /loan/chargeLenders` returns them. */
export interface ILoanChargeLender {
  participantUserId: number
  name: string
  /** True for the loan's own Lender standing in as a participant. */
  isLender?: boolean
}

/**
 * Which charges the grid lists. Blank is all of them; `Unpaid` is what is still owed and `Paid`
 * its complement - settled either by the isPaid flag (a payoff clears a loan's charges wholesale)
 * or by nothing being left on the balance. Must match the backend's LoanChargePaidStatus.
 */
export enum LoanChargePaidStatus {
  Unpaid = 'unpaid',
  Paid = 'paid',
}

export enum LoanChargeType {
  LoanCharges = 'loanCharges',
  UnpaidInterest = 'unpaidInterest',
  /**
   * Default-rate interest a payment assessed and did not collect. Its own bucket rather than part
   * of UnpaidInterest: the two are owed under different terms (the note rate against the default
   * spread), a servicer waives one without waiving the other, and a payoff quote has to be able to
   * say which of them it is collecting.
   */
  UnpaidDefaultInterest = 'unpaidDefaultInterest',
  UnpaidLateCharges = 'unpaidLateCharges',
}

/** A row of the configurable fee-category list behind the charge picker and the by-category reports. */
export interface ILoanChargeCategory {
  id: number
  value: string
  sortOrder: number
  isActive: boolean
  /** Non-empty on the categories the platform books charges into itself (NSF fees, invoices). */
  systemKey: string
  /** The split a new charge in this category starts with. */
  defaultSplits?: IFeeSplitLine[]
}

/**
 * The charges-filter option standing for charges with no category. The server appends it to the
 * used-category list only when the rows on screen actually include uncategorized charges, so it is
 * never offered against a fully categorized book. Must match the backend's UNCATEGORIZED.
 */
export const UNCATEGORIZED = 'Uncategorized'

/** How the charges summary buckets its rows. Must match the backend's LoanChargeGroupBy. */
export enum LoanChargeGroupBy {
  /** The charge's own label - "Extension Fee", "Forced Placed Insurance". */
  Description = 'description',
  /** The configurable fee category it was filed under. */
  Category = 'category',
}

/** One bucket of the charges summary: every charge of one kind, totalled. */
export interface ILoanChargeSummaryGroup {
  /** The charge type or category the bucket covers; the server names the empty one. */
  label: string
  /** How many charges it covers. */
  count: number
  originalBalance: number
  unpaidBalance: number
  accruedInterest: number
  totalDue: number
}

/** `GET /loan/chargeSummary`: the grid's charges, one line per charge type. */
export interface ILoanChargeSummary {
  groupBy: LoanChargeGroupBy
  groups: ILoanChargeSummaryGroup[]
  totals: Omit<ILoanChargeSummaryGroup, 'label'>
}
